/**
 * True when `err` looks like a failed dynamic-import / code-split chunk load —
 * the classic "stale hashed chunk after a redeploy" case where a tab still
 * running the old `index.html` requests a chunk filename that no longer exists.
 *
 * Detection is by message text, not by `name`: Vite throws a plain `TypeError`
 * for a failed dynamic import (so `err.name` is NOT `"ChunkLoadError"`), and the
 * message wording differs per browser. We also accept the webpack-style
 * `ChunkLoadError` name for completeness.
 *
 * Known limitation: a stale *CSS* chunk fails via a `<link>` error event with no
 * catchable throw — that path is not covered here (nor by any error boundary);
 * Vite's `vite:preloadError` event covers preloaded assets. See
 * docs/adr/0081-frontend-error-boundary-and-chunk-recovery.md.
 */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;

  const name = (err as { name?: unknown }).name;
  if (name === "ChunkLoadError") return true; // webpack-style

  const rawMessage =
    typeof err === "string"
      ? err
      : typeof (err as { message?: unknown }).message === "string"
        ? (err as { message: string }).message
        : "";
  if (!rawMessage) return false;

  const m = rawMessage.toLowerCase();
  return (
    m.includes("failed to fetch dynamically imported module") || // Chrome / Edge
    m.includes("error loading dynamically imported module") || // Firefox
    m.includes("importing a module script failed") || // Safari
    m.includes("dynamically imported module") // defensive catch-all
  );
}
